import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { getRankTierColor, getRankTierLabel } from '../constants/rankColors';

import { ReactComponent as RookieStarIcon }   from '../assets/icons/ranks/rookie-star.svg';
import { ReactComponent as BronzeStarIcon }   from '../assets/icons/ranks/bronze-star.svg';
import { ReactComponent as SilverStarIcon }   from '../assets/icons/ranks/silver-star.svg';
import { ReactComponent as GoldStarIcon }     from '../assets/icons/ranks/gold-star.svg';
import { ReactComponent as PlatinumHexIcon }  from '../assets/icons/ranks/platinum-hexagon.svg';

/** tier code に対応する SVG アイコンコンポーネントを返す。 */
const RankIconComponent: React.FC<{ tierCode?: string | null; size: number }> = ({ tierCode, size }) => {
  const style = { width: size, height: size, flexShrink: 0, display: 'block' };
  switch (tierCode) {
    case 'gold':     return <GoldStarIcon     style={style} />;
    case 'silver':   return <SilverStarIcon   style={style} />;
    case 'bronze':   return <BronzeStarIcon   style={style} />;
    case 'platinum': return <PlatinumHexIcon  style={style} />;
    case 'rookie':
    default:         return <RookieStarIcon   style={style} />;
  }
};

interface RankStarProps {
  /** ティアの code（rookie..platinum）。色・ラベルのフォールバック解決に使用 */
  tierCode?: string | null;
  /** サーバの skill_rank_tiers.color（あれば最優先＝単一の真実） */
  color?: string | null;
  /** サーバの tier 名（あれば最優先） */
  label?: string | null;
  /** 星のサイズ（px） */
  size?: number;
  /** ラベルチップを表示するか */
  showLabel?: boolean;
}

/**
 * スキルランクのアイコン（＋任意のラベルチップ）。
 *
 * platinum はヘキサゴン SVG、それ以外はローポリ星 SVG を使用。
 * 色の優先順位: props.color（サーバ値＝真実） > tierCode からのフォールバック（付録C）。
 * ラベルの優先順位: props.label（サーバ値） > tierCode からのフォールバック。
 */
const RankStar: React.FC<RankStarProps> = ({
  tierCode,
  color,
  label,
  size = 20,
  showLabel = false,
}) => {
  const resolvedColor = color || getRankTierColor(tierCode);
  const resolvedLabel = label || getRankTierLabel(tierCode);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <RankIconComponent tierCode={tierCode} size={size} />
      {showLabel && (
        <Chip
          label={resolvedLabel}
          size="small"
          sx={{
            bgcolor: `${resolvedColor}1f`,
            color: resolvedColor,
            fontWeight: 600,
            fontSize: '0.75rem',
            border: `1px solid ${resolvedColor}`,
            height: 22,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      )}
    </Box>
  );
};

/**
 * ラベルをチップ無しでテキスト表示する軽量版（一覧の名前横などで使用）。
 */
export const RankStarInline: React.FC<RankStarProps> = ({
  tierCode,
  color,
  label,
  size = 18,
}) => {
  const resolvedColor = color || getRankTierColor(tierCode);
  const resolvedLabel = label || getRankTierLabel(tierCode);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <RankIconComponent tierCode={tierCode} size={size} />
      <Typography variant="caption" fontWeight="bold" sx={{ color: resolvedColor }}>
        {resolvedLabel}
      </Typography>
    </Box>
  );
};

export default RankStar;
